import { Section } from "@pulse/features/Sections";
import { useCurrentQuestions, useCurrentSurvey } from "@pulse/hooks/surveyHooks";
import { QuestionProvider } from "./QuestionProvider";
import { RatingQuestion } from "./RatingQuestion";
import { TextInputQuestion } from "./TextInputQuestion";
import { MultipleChoiceQuestion } from "./MultipleChoiceQuestion";
import { QuestionType, type Question } from "@pulse/api/surveyApi";
import EditQuestionFormProvider from "./EditQuestionFormProvider";
import { useEditQuestionForm, useQuestion } from "./hooks";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "common-frontend-components";
import { useReorderAnimation } from "frontend-components";
import { useReorderQuestions, useSurveySavedNotification } from "../../hooks";

function QuestionSection() {
    const { question } = useQuestion();
    const { t } = useTranslation();
    const form = useEditQuestionForm();

    const questionType = question.questionType;

    const questionComponent = useMemo(() => {
        switch (questionType) {
            case QuestionType.RATING:
                return <RatingQuestion />;
            case QuestionType.TEXT:
                return <TextInputQuestion />;
            case QuestionType.MULTIPLE_CHOICE:
            case QuestionType.MULTI_SELECT:
                return <MultipleChoiceQuestion />;
            default: {
                const _exhaustiveCheck: never = questionType;
                void _exhaustiveCheck;
                return <div>{t("question.unsupportedQuestionTypeError")}</div>;
            }
        }
    }, [questionType, t]);

    return (
        <Section sectionId={question.id} onClose={form.handleSubmit}>
            {questionComponent}
        </Section>
    );
}

function QuestionRow({
    children,
    index,
    questions,
    isPending,
    itemRef,
    onMove,
}: {
    children: ReactNode;
    index: number;
    questions: Question[];
    isPending: boolean;
    itemRef: (node: HTMLDivElement | null) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
}) {
    const moveUpDisabled = isPending || index === 0;
    const moveDownDisabled = isPending || index === questions.length - 1;
    const { t } = useTranslation();

    return (
        <div ref={itemRef} className="flex items-start gap-2">
            <div
                className="flex shrink-0 flex-col items-center gap-1 pt-8"
                onMouseDown={(event) => event.stopPropagation()}
                onMouseUp={(event) => event.stopPropagation()}
            >
                <Button
                    type="button"
                    variant="unstyled"
                    shape="square"
                    className="h-8 w-8 text-on-background/60 hover:text-on-background disabled:opacity-30"
                    aria-label={t("question.reorder.moveUp")}
                    disabled={moveUpDisabled}
                    onClick={() => onMove(index, index - 1)}
                >
                    <FontAwesomeIcon icon={faArrowUp} />
                </Button>
                <Button
                    type="button"
                    variant="unstyled"
                    shape="square"
                    className="h-8 w-8 text-on-background/60 hover:text-on-background disabled:opacity-30"
                    aria-label={t("question.reorder.moveDown")}
                    disabled={moveDownDisabled}
                    onClick={() => onMove(index, index + 1)}
                >
                    <FontAwesomeIcon icon={faArrowDown} />
                </Button>
            </div>
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
}

export default function QuestionSections() {
    const { data: questions } = useCurrentQuestions();
    const { data: survey } = useCurrentSurvey();
    const reorderQuestions = useReorderQuestions(survey.id);
    const surveySavedNotification = useSurveySavedNotification();
    const questionIds = questions?.map((question) => question.id) ?? [];
    const { capturePositions, setItemRef } = useReorderAnimation(questionIds);

    const reorder = (reorderedQuestions: Question[]) => {
        const promise = reorderQuestions.mutateAsync(
            reorderedQuestions.map((question) => question.id),
        );
        surveySavedNotification(promise);
    };

    const handleMove = (fromIndex: number, toIndex: number) => {
        if (!questions || toIndex < 0 || toIndex >= questions.length || fromIndex === toIndex) {
            return;
        }

        const reorderedQuestions = [...questions];
        const [movedQuestion] = reorderedQuestions.splice(fromIndex, 1);
        reorderedQuestions.splice(toIndex, 0, movedQuestion);
        capturePositions();
        reorder(reorderedQuestions);
    };

    if (!questions || questions.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            {questions.map((question, idx) => (
                <QuestionRow
                    key={question.id}
                    index={idx}
                    questions={questions}
                    isPending={reorderQuestions.isPending}
                    itemRef={setItemRef(question.id)}
                    onMove={handleMove}
                >
                    <QuestionProvider questionId={question.id} idx={idx}>
                        <EditQuestionFormProvider>
                            <QuestionSection />
                        </EditQuestionFormProvider>
                    </QuestionProvider>
                </QuestionRow>
            ))}
        </div>
    );
}
